import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Ledger } from "@common/contracts/ledger.contract";

export interface LedgerSwitchTarget {
    referenceNumber: string;
    ledgerBasis: AccountingLedgerBasis;
}

interface TransactionJournalEntryForSwitch {
    referenceNumber: string;
    accountingLedger: {
        id: string;
        entityId: number;
        basis: AccountingLedgerBasis;
    };
}

interface TransactionForLedgerSwitch {
    journalEntries?: TransactionJournalEntryForSwitch[] | null;
}

/**
 * Maps ledger id -> the sibling journal entry (referenceNumber + basis) a
 * transaction-sourced JE has in that ledger, scoped to the drawer's entity.
 *
 * A transaction posts one 2-line entry per ledger it participates in: cash
 * always, accrual only when it has an override account for that ledger (see
 * journal-entries-builder.service.ts). Drilling down from a transaction into
 * one of those entries previously locked the ledger selector entirely; this
 * lets the drawer offer a toggle to whichever sibling ledgers actually exist.
 */
export function buildLedgerSwitchTargets({
    transactions,
    entityId,
}: {
    transactions: TransactionForLedgerSwitch[];
    entityId: number;
}): Map<string, LedgerSwitchTarget> {
    const targets = new Map<string, LedgerSwitchTarget>();
    for (const transaction of transactions) {
        for (const je of transaction.journalEntries ?? []) {
            if (je.accountingLedger.entityId !== entityId) {
                continue;
            }
            targets.set(je.accountingLedger.id, {
                referenceNumber: je.referenceNumber,
                ledgerBasis: je.accountingLedger.basis,
            });
        }
    }
    return targets;
}

/**
 * Resolves the target to switch to, or undefined when the ledger has no
 * sibling entry or is already the one showing (no-op — avoids reopening the
 * drawer on the ledger it's already on).
 *
 * The no-op check compares ledger ids, not reference numbers: cash and accrual
 * entries for a split transaction can share a reference number, so matching on
 * referenceNumber would wrongly treat a click on the other ledger as a no-op.
 */
export function resolveLedgerSwitchTarget({
    ledgerSwitchTargets,
    targetLedgerId,
    currentLedgerId,
}: {
    ledgerSwitchTargets: ReadonlyMap<string, LedgerSwitchTarget>;
    targetLedgerId: string;
    currentLedgerId: string | undefined;
}): LedgerSwitchTarget | undefined {
    if (targetLedgerId === currentLedgerId) {
        return undefined;
    }
    return ledgerSwitchTargets.get(targetLedgerId);
}

/**
 * Ledgers with no sibling entry for this transaction stay visible in the
 * selector (so the user can see cash/accrual both exist) but disabled, with
 * a tooltip explaining why — instead of disappearing or silently no-op-ing.
 */
export function buildLedgerOptionOverrides({
    ledgers,
    ledgerSwitchTargets,
}: {
    ledgers: Array<Pick<Ledger, "id">>;
    ledgerSwitchTargets: ReadonlyMap<string, LedgerSwitchTarget> | undefined;
}): Map<string, { disabled?: boolean; tooltip?: string }> | undefined {
    // Distinguish "data not loaded / request failed" from "confirmed no entry".
    // A transaction JE always has at least its own cash entry once details
    // load, so an empty (or missing) target map means the data is unavailable
    // — keep every ledger enabled rather than disabling the one being viewed
    // and claiming it has no journal entry.
    if (!ledgerSwitchTargets || ledgerSwitchTargets.size === 0) {
        return undefined;
    }
    const overrides = new Map<
        string,
        { disabled?: boolean; tooltip?: string }
    >();
    for (const ledger of ledgers) {
        if (!ledgerSwitchTargets.has(ledger.id)) {
            overrides.set(ledger.id, {
                disabled: true,
                tooltip: "No journal entry for this transaction",
            });
        }
    }
    return overrides;
}
